import { Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  EventMessage,
  InteractionStatus,
  EventType as MsalType,
} from '@azure/msal-browser';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter } from 'rxjs';
import { ConsentDialogContent } from './pages/consent/components/consent-dialog-content/consent-dialog-content';
import { ConsentState } from './pages/consent/services/consent-state';
import { ConsultationFacade } from './pages/consultation/services/consultation/consultation-facade';
import { Dialog } from './shared/components/dialog/dialog';
import { Header } from './shared/components/header/header';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    Header,
    TranslocoDirective,
    Dialog,
    ConsentDialogContent,
    TranslocoDirective,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authService = inject(MsalService);
  private readonly consentState = inject(ConsentState);
  private readonly consultationFacade = inject(ConsultationFacade);
  private readonly router = inject(Router);

  public consentDialogOpen = this.consentState.showDialog;

  constructor() {
    this.authService.handleRedirectObservable().subscribe();

    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === MsalType.ACCOUNT_ADDED ||
            msg.eventType === MsalType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe(() => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
  }

  public async onConsentAgreed(): Promise<void> {
    await this.consultationFacade.submitConsentAndProceed();
  }

  public onConsentDialogClose(): void {
    this.consentState.closeDialog();
  }
}
